import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import React, { useLayoutEffect, useState, useMemo, useCallback } from "react";
import { Fade, Paper, TextField } from "@mui/material";
import "./Field.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderGrid, faFolderOpen } from "@fortawesome/pro-solid-svg-icons";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import EntityTable from "../entityTable/EntityTable";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { FieldProps } from "./Field";
import MirageFormControl from "./MirageFormControl";
import { LookupService } from "@mirage/services/LookupService";
import { IEntityPageResult } from "@shared/mirage/core/contracts/IEntityPageResult";
import LookupFieldAsPicklist from "./LookupFieldAsPicklist";
import { Filter } from "@shared/mirage/entityTable/Filter";
import { TextNavigate } from "../buttons/TextNavigate/TextNavigate";
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LookupFieldProps extends FieldProps {}
const lookupService = new LookupService();

const LookupField: React.FC<LookupFieldProps> = (props) => {
    const {
        label,
        mode,
        onChange,
        relatedObject,
        relatedRecord,
        isRequired,
        customPageRequest,
        readOnly,
        errorMsg,
        customColumns,
        asPicklist,
        fetchOnOpen,
        sortModel,
        filter,
        forceFetch,
        navigateTo,
        containerClassName,
        showAllUsersFromApplication,
        helpText,
    } = props;
    const [openLookup, setOpenLookup] = useState(false);
    const currentVal = (relatedRecord && relatedRecord?.name) || "";
    const isEdit = mode === "edit";
    const columns = useMemo(() => (customColumns ? customColumns : [{ field: "name", headerName: "Name" }]), [customColumns]);
    const showAsPicklist = (asPicklist || columns.length === 1) && relatedObject !== "ATTACHMENT";
    const handleDeleteLookup = () => {
        if (onChange) {
            onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, null);
        }
    };

    useLayoutEffect(() => {
        if (mode !== "edit") {
            setOpenLookup(false);
        }
    }, [mode]);

    return (
        <>
            <MirageFormControl label={label} mode={mode} isRequired={isRequired} errorMsg={errorMsg} className={containerClassName} helpText={helpText}>
                {getFieldByMode()}
            </MirageFormControl>
            {!showAsPicklist ? (
                <LookupContent
                    customPageRequest={customPageRequest}
                    isEdit={isEdit}
                    label={label}
                    openLookup={openLookup}
                    setOpenLookup={setOpenLookup}
                    relatedObject={relatedObject}
                    onChange={onChange}
                    customColumns={customColumns}
                    sortModel={sortModel}
                    filter={filter}
                    forceFetch={forceFetch}
                />
            ) : null}
        </>
    );

    function getFieldByMode() {
        if (!isEdit || navigateTo) {
            let val = currentVal;
            if (asPicklist && relatedRecord && customColumns?.length) {
                val = customColumns.map((c) => relatedRecord[c.field]).join(" - ");
            }
            const text = navigateTo ? <TextNavigate disabled={readOnly} text={val as string} navigateTo={navigateTo} /> : (val as string);
            return <span className="mirageFormControl__field">{text}</span>;
        }
        if (showAsPicklist) {
            return (
                <LookupFieldAsPicklist
                    columns={columns}
                    onChange={onChange}
                    relatedRecord={relatedRecord}
                    relatedObject={relatedObject}
                    customPageRequest={customPageRequest}
                    errorMsg={errorMsg}
                    isRequired={isRequired}
                    fetchOnOpen={fetchOnOpen}
                    readOnly={readOnly}
                    filter={filter}
                    forceFetch={forceFetch}
                    sortModel={sortModel}
                    customColumns={customColumns}
                    showAllUsersFromApplication={showAllUsersFromApplication}
                />
            );
        }
        return (
            <TextField
                disabled={readOnly}
                error={(isRequired && !currentVal) || !!errorMsg}
                InputProps={{ endAdornment: getLookupIcons(), readOnly: true }}
                value={currentVal}
                className="mirageFormControl__field"
                unselectable="on"
                type="text"
                size="small"
            />
        );
    }

    function getLookupIcons() {
        if (isEdit) {
            return (
                <>
                    {relatedRecord?.id ? (
                        <IconButton aria-label="delete lookup" disabled={readOnly} onClick={handleDeleteLookup}>
                            <FontAwesomeIcon icon={MirageTheme.icons.closeIcon} style={MirageTheme.icons.sizeS} />
                        </IconButton>
                    ) : null}
                    <Divider className="mirageFormControl__divider" orientation="vertical" />
                    <IconButton type="button" aria-label="search" disabled={readOnly} onClick={() => setOpenLookup((prev) => !prev)}>
                        <FontAwesomeIcon icon={openLookup ? faFolderOpen : faFolderGrid} style={MirageTheme.icons.sizeS} />
                    </IconButton>
                </>
            );
        }
        return null;
    }
};

const LookupContent: React.FC<LookupContentProps> = ({
    customPageRequest,
    openLookup,
    setOpenLookup,
    label,
    isEdit,
    onChange,
    relatedObject = "USERS_",
    customColumns,
    sortModel,
    filter = {} as Filter,
    forceFetch,
}) => {
    const columns = customColumns ? customColumns : [{ field: "name", headerName: "Name" }];
    const onRecordSelected = async (selectedRecord: LookupSelectedRecord) => {
        if (onChange) {
            setOpenLookup((prev) => !prev);
            if (relatedObject === "ATTACHMENT" || relatedObject === "USERS_") {
                onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, selectedRecord);
            } else {
                const record = selectedRecord ? await lookupService.getRecordById({ recordId: selectedRecord.id, relatedObject }) : null;
                onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, record);
            }
        }
    };
    const onPageRequest = useCallback(
        async (pr: IEntityPageRequest) => {
            if (customPageRequest) {
                return await customPageRequest(pr);
            }
            if (openLookup) {
                const result = await lookupService.pageRequest({ ...pr, relatedObject });
                return result;
            }
        },
        [customPageRequest, openLookup, relatedObject]
    );

    if (isEdit && openLookup) {
        return (
            <Fade in={openLookup} timeout={600}>
                <Paper elevation={6} className="mirageFormControl__field--lookup">
                    <EntityTable
                        title=""
                        entityName={relatedObject as string}
                        onRecordSelected={onRecordSelected}
                        noRecordsImage
                        columns={columns}
                        key={`lookup-entityTable-${label}`}
                        onPageRequest={onPageRequest}
                        stateKey={`lookup-entityTable-${label}-state`}
                        sortModel={sortModel ? sortModel : [{ field: columns[0].field, sort: "asc" }]}
                        forceFetch={forceFetch}
                        filter={filter}
                        hideRowsPerPage
                        showQuickSearch={true}
                        toolbarOptions={{ disableColumns: true, disableFilter: true }}
                    />
                </Paper>
            </Fade>
        );
    }
    return null;
};

export type LookupSelectedRecord = {
    id: number;
    name: string;
    [key: string]: string | Date | number | null;
};

interface LookupContentProps extends Pick<LookupFieldProps, "onChange" | "label" | "relatedObject" | "relatedRecord" | "customColumns" | "sortModel" | "filter" | "forceFetch"> {
    openLookup: boolean;
    setOpenLookup: React.Dispatch<React.SetStateAction<boolean>>;
    isEdit: boolean;
    customPageRequest?: (pageRequest: unknown & IEntityPageRequest) => Promise<IEntityPageResult<any>>;
}

export default LookupField;
